import loadable from '@loadable/component'
import {
    PlayerEvent,
    VideoDetails,
    VideoQueueItemMeta,
} from '@news-mono/web-common'
import React, { useEffect, useState } from 'react'
import { MediaMode } from '../../../../cards/CardMedia/CardMedia'
import {
    AutoplayOptions,
    GetVideoQueue,
    VideoOptions,
} from '../../../../content/Video/PlayerInterface'
import { FeatureToggle } from '../../../../feature-toggling/Toggle/Toggle'
import { FigcaptionRenderOptions } from '../../../../publication/ArticleImage/ArticleImage'
import { createRenderTarget } from '../../../../render-target'
import { PNRssArticleVideo } from './PNArticleVideo.rss'
import { PNArticleImage } from '../PNArticleImage/PNArticleImage'
import PNAmpArticleVideo from './PNArticleVideo.amp'
import { useStickyVideo } from './hook/useStickyVideo'
import {
    StyledStickyCell,
    StyledStickyObservedCell,
} from './PNArticleVideo.styled'

export const PNLazyWebArticleVideo = loadable(
    () => import('./PNArticleVideo.web'),
)

export interface PNVideoArticle {
    isHeroVideo?: boolean
    isVideoStickyEnabled?: boolean
    publicationPathname?: string
}

export interface PNArticleVideoProps extends PNVideoArticle {
    showTitles: boolean
    videoDetails: VideoDetails
    videoOptions: VideoOptions
    autoplayOptions: AutoplayOptions | false
    autoplayNextOptions: AutoplayOptions | false
    pauseOverlay: boolean
    getVideoQueue: GetVideoQueue
    byline?: string
    captionText?: string
    credit?: string
    kind?: string
    figcaptionRenderOptions?: FigcaptionRenderOptions
    onEvent: (event: PlayerEvent) => void
    playButtonText?: string
    smallPlayButton?: boolean
    mediaMode?: MediaMode
    isVideoHub?: boolean
    publicUrl?: string
    onPlayNextVideo?: (nextVideo: VideoQueueItemMeta) => void
    adElement?: JSX.Element
    setVideoRendered?: () => void

    isVideoCurrentlySticky?: boolean
}

const PNArticleVideoRenderTarget = createRenderTarget<PNArticleVideoProps>(
    'PNArticleVideo',
    {
        web: PNLazyWebArticleVideo,
        // todo: remove this once we implement amp redirect since amp is no longer needed
        amp: PNAmpArticleVideo,
        rss: PNRssArticleVideo,
        preview: PNLazyWebArticleVideo,
        app: PNLazyWebArticleVideo,
    },
)
export const PNArticleVideo = (props: PNArticleVideoProps) => {
    const [videoContainerHeight, setVideoContainerHeight] = useState(0)

    const { isVideoStickyEnabled } = props

    const { isCurrentlySticky, impressionUnavailableRef, stickyRef } =
        useStickyVideo({
            userInteracted: true,
        })

    const getStickyVideoWrapper = (children: JSX.Element) =>
        isVideoStickyEnabled ? (
            <StyledStickyObservedCell
                ref={impressionUnavailableRef}
                style={
                    videoContainerHeight > 0
                        ? { height: videoContainerHeight }
                        : {}
                }
            >
                <StyledStickyCell ref={stickyRef}>{children}</StyledStickyCell>
            </StyledStickyObservedCell>
        ) : (
            children
        )

    // Set the height of the video container so when it becomes sticky it doesn't cause layout shift
    useEffect(() => {
        const videoContainer = stickyRef.current
        if (!videoContainer) return

        setVideoContainerHeight(videoContainer.getBoundingClientRect().height)
    }, [stickyRef])

    return (
        <FeatureToggle
            feature={'brightcove-player'}
            on={() =>
                getStickyVideoWrapper(
                    <PNArticleVideoRenderTarget
                        {...props}
                        isVideoCurrentlySticky={isCurrentlySticky}
                    />,
                )
            }
            off={() => {
                if (!props.videoDetails.posterImage) {
                    return null
                }
                return (
                    <PNArticleImage
                        image={props.videoDetails.posterImage}
                        figCaption={{
                            byline: props.byline,
                            captionText: props.captionText,
                            credit: props.credit,
                            kind: 'image',
                        }}
                        figcaptionRenderOptions={props.figcaptionRenderOptions}
                        isHeroImage={true}
                    />
                )
            }}
        />
    )
}
